/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { useForm, Controller } from 'react-hook-form';
import { Typography, Switch, Button } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import FormControlLabel from '@mui/material/FormControlLabel';
import { GaussianBlurBackgroundProcessor, isSupported as isBlurSupported } from '@twilio/video-processors';

import { VideoPreview, InputLabel, Select, MicrophoneTest, TextFieldControl } from 'components/elements';
import { PersonIcon, CameraIcon, MicrophoneIcon, SpeakerIcon, CameraDisabledIcon, MicrophoneDisabledIcon } from 'components/elements/Icons';
import { VideoSessionInstructions } from 'components/modules';
import { useMediaDevices, useVideoContext, useDeviceDetect, useOrganization, useVideoTokenActions } from 'hooks';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function VideoCallSettings({ children, roomName, sessionToken, token, isWaitingRoom, sessionName, accountName }) {
  const classes = useStyles();
  const queryParameters = new URLSearchParams(window.location.search);
  const [videoTrack, setVideoTrack] = useState();
  const [blurBackground, setBlurBackground] = useState();
  const { t } = useTranslation('labels');
  const { isMobile } = useDeviceDetect();
  const oid = queryParameters.get('oid');
  const { data: organization } = useOrganization(oid);
  const { updateSessionName } = useVideoTokenActions(sessionToken, roomName);
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting, settings, setSettings, getLocalVideoTrack } = useVideoContext();
  const { control, watch, setValue, resetField, handleSubmit, getValues } = useForm({
    mode: 'onSubmit',
    defaultValues: { ...settings, sessionName: sessionName || accountName },
  });
  useEffect(() => {
    const subscription = watch((value) => setSettings(value));
    return () => subscription.unsubscribe();
  }, [watch]);

  const videoInputId = watch('videoInputId');
  const environmentVideoInputId = watch('environmentVideoInputId');
  const audioInputId = watch('audioInputId');
  const videoInputEnabled = watch('videoInputEnabled');
  const audioInputEnabled = watch('audioInputEnabled');
  const {
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    environmentalVideoInputDevices,
    devicesAvailability: { isCameraPermissionDenied, isMicrophonePermissionDenied },
  } = useMediaDevices();

  useEffect(async () => {
    if (!isBlurSupported) return;

    const bg = new GaussianBlurBackgroundProcessor({
      assetsPath: '/assets/twilio/',
      maskBlurRadius: 10,
      blurFilterRadius: 5,
    });
    await bg.loadModel();
    setBlurBackground(bg);
  }, []);

  useEffect(async () => {
    if (videoInputId) {
      const track = await getLocalVideoTrack();
      setVideoTrack(track);
    }
  }, [videoInputId]);

  useEffect(async () => {
    setValue('blurInput', isBlurSupported && organization.isBlurOnWhenJoiningCall);
  }, [organization.isBlurOnWhenJoiningCall]);

  useEffect(async () => {
    if (videoTrack && blurBackground) {
      const videoParent = document.getElementById('local-media');
      if (videoParent) {
        await videoTrack.attach();
        if (videoTrack.processor && !settings.blurInput) {
          await videoTrack.removeProcessor(blurBackground);
        }
        if (!videoTrack.processor && settings.blurInput) {
          await videoTrack.addProcessor(blurBackground);
        }
        videoParent.innerHTML = '';
        videoParent.appendChild(videoTrack.attach());
        const video = videoParent.querySelectorAll('video')[0];
        video.classList.add(classes.webcamVideo);
      }
    }
  }, [videoTrack, blurBackground, settings.blurInput]);

  useEffect(() => {
    if (audioOutputDevices.length && getValues('audioOutputId') === '') {
      setValue('audioOutputId', audioOutputDevices.some((x) => x.value === 'default') ? 'default' : audioOutputDevices[0]?.value);
    }
  }, [audioOutputDevices]);

  useEffect(() => {
    if (isCameraPermissionDenied) setValue('videoInputEnabled', false);
    else resetField('videoInputEnabled');
  }, [isCameraPermissionDenied]);

  useEffect(() => {
    if (isMicrophonePermissionDenied) setValue('audioInputEnabled', false);
    else resetField('audioInputEnabled');
  }, [isMicrophonePermissionDenied]);

  useEffect(() => {
    if (videoInputEnabled && videoInputId === '' && videoInputDevices.length && !isCameraPermissionDenied) {
      setValue('videoInputId', videoInputDevices.some((x) => x.value === 'default') ? 'default' : videoInputDevices[0]?.value);
    }
    if (videoInputEnabled && environmentVideoInputId === '' && environmentalVideoInputDevices.length && !isCameraPermissionDenied) {
      setValue('environmentVideoInputId', environmentalVideoInputDevices[0]?.value);
    }
  }, [videoInputDevices, videoInputId, videoInputEnabled, isCameraPermissionDenied]);

  useEffect(() => {
    if (audioInputEnabled && audioInputId === '' && audioInputDevices.length && !isMicrophonePermissionDenied) {
      setValue('audioInputId', audioInputDevices.some((x) => x.value === 'default') ? 'default' : audioInputDevices[0]?.value);
    }
  }, [audioInputDevices, audioInputId, audioInputEnabled, isMicrophonePermissionDenied]);

  const disableSubmit = isAcquiringLocalTracks || isConnecting || !roomName || !token;
  const handleJoin = async () => videoConnect(token, roomName);
  // eslint-disable-next-line react/no-unstable-nested-components
  function AudioLabelIcon() {
    return !isWaitingRoom ? <MicrophoneIcon classes={classes.labelIcon} /> : <MicrophoneDisabledIcon classes={classes.labelIcon} />;
  }
  // eslint-disable-next-line react/no-unstable-nested-components
  function VideoLabelIcon() {
    return !isWaitingRoom ? <CameraIcon classes={classes.labelIcon} /> : <CameraDisabledIcon classes={classes.labelIcon} />;
  }

  function handleBlurEffect() {
    setValue('blurInput', !settings.blurInput);
  }

  function updateName() {
    let name = getValues('sessionName');
    if (name === '' || name == null) name = accountName;
    updateSessionName(name);
  }

  return (
    <form className={cn(classes.waitingCallSettingsForm, { [classes.videoCallSettingsForm]: !isWaitingRoom })} onSubmit={handleSubmit(handleJoin)}>
      <div className={classes.scroll}>
        {isWaitingRoom ? (
          <div className={classes.cameraTest}>
            <div className={classes.videoPreviewItem}>
              <VideoPreview videoDeviceId={videoInputEnabled && videoInputId ? videoInputId : ''} cameraEnabled={!isCameraPermissionDenied} />
            </div>
            <div className={classes.item}>{children}</div>
          </div>
        ) : null}
        <div className={cn(classes.testingSettings, { [classes.centeredTestingSettings]: !isWaitingRoom })}>
          <div className={cn(classes.item, { [classes.centeredItem]: !isWaitingRoom })}>
            {isWaitingRoom ? t('settings') : null}
            <div className={classes.select}>
              <FormControlLabel className={classes.label} control={<div />} label={<PersonIcon classes={classes.labelIcon} />} labelPlacement="start" />
              <TextFieldControl name="sessionName" control={control} labelText={t('name')} isEditMode />
              <Button className={classes.updateNameButton} variant="contained" onClick={() => updateName()}>
                <Typography variant="span" component="span">
                  {t('update')}
                </Typography>
              </Button>
            </div>
            <div className={classes.select}>
              <Controller
                name="videoInputEnabled"
                control={control}
                render={({ field: { ref, value, ...restField } }) => (
                  <FormControlLabel
                    className={classes.label}
                    disabled={isCameraPermissionDenied}
                    control={isWaitingRoom ? <div /> : <Switch onChange={() => setValue('videoInputEnabled', !videoInputEnabled)} checked={videoInputEnabled} {...restField} />}
                    label={videoInputEnabled ? <CameraIcon classes={classes.labelIcon} /> : <VideoLabelIcon />}
                    labelPlacement="start"
                  />
                )}
              />
              <Controller
                name="videoInputId"
                control={control}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <Select
                    options={videoInputDevices}
                    defaultValue=""
                    label={<InputLabel text={t('camera')} />}
                    helperText={error ? error.message : null}
                    error={!!error}
                    {...restField}
                  />
                )}
              />
            </div>
            <div className={classes.select}>
              <Controller
                name="audioInputEnabled"
                control={control}
                render={({ field: { ref, value, ...restField } }) => (
                  <FormControlLabel
                    className={classes.label}
                    disabled={isMicrophonePermissionDenied}
                    control={isWaitingRoom ? <div /> : <Switch onChange={() => setValue('audioInputEnabled', !audioInputEnabled)} checked={audioInputEnabled} {...restField} />}
                    label={audioInputEnabled ? <MicrophoneIcon classes={classes.labelIcon} /> : <AudioLabelIcon />}
                    labelPlacement="start"
                  />
                )}
              />
              <Controller
                name="audioInputId"
                control={control}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <Select
                    options={audioInputDevices}
                    label={<InputLabel text={t('microphone')} />}
                    helperText={error ? error.message : null}
                    error={!!error}
                    defaultValue=""
                    {...restField}
                  />
                )}
              />
            </div>
            {(isWaitingRoom || !isMobile) && (
              <div className={classes.micTest}>
                <MicrophoneTest microphoneId={audioInputEnabled ? audioInputId : ''} />
              </div>
            )}
            {isBlurSupported && (
              <div className={classes.select}>
                <Controller
                  name="blurInput"
                  control={control}
                  render={() => (
                    <FormControlLabel
                      className={classes.label}
                      control={
                        <Checkbox
                          data-testid="checkbox_blur"
                          name="blur"
                          color="primary"
                          role="checkbox"
                          checked={settings.blurInput}
                          onChange={() => {
                            handleBlurEffect();
                          }}
                        />
                      }
                      label={t('blur_video')}
                      labelPlacement="end"
                    />
                  )}
                />
              </div>
            )}
            <div className={classes.select}>
              <FormControlLabel className={classes.label} control={<div />} label={<SpeakerIcon classes={classes.labelIcon} />} labelPlacement="start" />
              <Controller
                name="audioOutputId"
                control={control}
                render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                  <Select
                    options={audioOutputDevices}
                    label={<InputLabel text={t('speaker')} />}
                    helperText={error ? error.message : null}
                    error={!!error}
                    defaultValue=""
                    {...restField}
                  />
                )}
              />
            </div>
          </div>
          {isWaitingRoom ? (
            <div className={classes.item}>
              <div className={classes.joinSession}>
                <Button className={classes.joinButton} type="submit" variant="contained" disabled={disableSubmit}>
                  <CameraIcon classes={classes.joinButtonIcon} />
                  <Typography className={classes.controlButtonText} variant="span" component="span">
                    {t('join')}
                  </Typography>
                </Button>
                <VideoSessionInstructions />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </form>
  );
}

VideoCallSettings.propTypes = {
  children: PropTypes.element,
  isWaitingRoom: PropTypes.bool,
  roomName: PropTypes.string.isRequired,
  sessionToken: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  sessionName: PropTypes.string,
  accountName: PropTypes.string,
};

VideoCallSettings.defaultProps = {
  isWaitingRoom: false,
  children: null,
  sessionName: '',
  accountName: '',
};

export default VideoCallSettings;
